import { useState } from 'react';
import { imageUrl } from '../../lib/format';
import { useSession } from '../../state/session';
import { Icon } from '../ui/Icon';
import { GlobalStatus } from './GlobalStatus';
import styles from './ProfilePicker.module.css';

/**
 * The screen a player lands on when the server hosts more than one realm.
 *
 * Every card is shown to everyone on purpose — a player who has only ever
 * played with one creator still sees the others exist, which is the cheapest
 * cross-sell this app will ever run. Each profile is a separate economy, so
 * picking one scopes every request that follows; switching later starts from
 * this screen again (see the player's profile page).
 *
 * The photographs carry it. A name in a box asks someone to read; a face asks
 * them to choose — so the thumbnail is the card, and the name sits under it.
 */

/** A creator's photo, falling back to her initial when there isn't one yet. */
function Portrait({ src, name }: { src: string | null; name: string }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span className={styles.portrait} aria-hidden="true">
        <span className={styles.initial}>{name.slice(0, 1).toUpperCase() || '?'}</span>
      </span>
    );
  }

  return (
    <span className={styles.portrait}>
      <img
        className={styles.photo}
        src={imageUrl(src)}
        alt=""
        onError={() => setFailed(true)}
        loading="lazy"
      />
    </span>
  );
}

export function ProfilePicker() {
  const { profiles, selectProfile, guild } = useSession();

  return (
    <div className={styles.screen}>
      <div className={styles.inner}>
        <span className={styles.mark}>
          {guild?.iconUrl ? (
            <img className={styles.markImage} src={imageUrl(guild.iconUrl)} alt="" />
          ) : (
            <Icon name="logo" size={34} />
          )}
        </span>
        <h1 className={styles.title}>Choose your realm</h1>
        <p className={styles.message}>
          Each realm is one creator&apos;s own wheels, credits and rewards. Your balance in one is
          yours there alone — your status across all of them is added up for the server.
        </p>
        <div className={styles.cards}>
          {profiles.map((profile) => (
            <button
              key={profile.id}
              type="button"
              className={styles.card}
              onClick={() => selectProfile(profile.id)}
            >
              <Portrait src={profile.avatar} name={profile.name} />
              <span className={styles.name}>{profile.name}</span>
              <span className={styles.enter}>
                Enter
                <Icon name="chevronRight" size={14} />
              </span>
            </button>
          ))}
        </div>

        {/* The main menu's own panel: your standing across every realm in the
            server — the status that becomes a role. It sits under the realm
            choice, and more of the player's profile will land beside it here. */}
        <GlobalStatus />
      </div>
    </div>
  );
}
